import { PARAMETER_TYPE } from "../../scheme/components/analytics/components/table/interfaces";
import type { IChartData, IMeasuresResponse, IPointResponse } from "../../scheme/components/analytics/interfaces";
import { transformMeasuresDataMultiplePoints } from "../../scheme/components/analytics/mappers/trendMappers";
import { MULTI_ANALYSIS_MEASURE_TYPE, type IStructureTreePoint } from "../types";
import { applyActiveMeasureCellFilters, matchesTemplateFilters } from "./data-marks";
import { denormalizeMultiAnalysisGlobalRange } from "./range-frame";
import { formatFrequencyRangeParam } from "./template-filters";

export type MultiAnalysisTimeValue = Date | string | null | undefined;

export interface IMultiAnalysisTimeRange {
  start: MultiAnalysisTimeValue;
  end: MultiAnalysisTimeValue;
}

export interface IMultiAnalysisRequestTimeRange {
  startTimestamp: string;
  endTimestamp: string;
}

export interface IMultiAnalysisMeasureOption {
  title: string;
  description: string | null;
  useValue2: boolean;
  type: number | null;
  rangeType: number | null;
}

export interface IMultiAnalysisMeasureDataResponseItem {
  name: string;
  data: IMeasuresResponse[];
}

export type IMultiAnalysisMeasureDataResponse = Record<string, IMultiAnalysisMeasureDataResponseItem | undefined>;

export const filterMeasureOptionsByCellFilters = (
  measureOptions: readonly IMultiAnalysisMeasureOption[],
  filters: Record<string, unknown>,
): IMultiAnalysisMeasureOption[] =>
  measureOptions.filter(option =>
    matchesTemplateFilters(
      {
        Name: option.title,
        RangeType: option.rangeType,
        Type: option.type,
      },
      filters,
    ),
  );

export const resolveMultiAnalysisTrendMeasureOptions = (
  measureOptions: readonly IMultiAnalysisMeasureOption[],
  measureType: MULTI_ANALYSIS_MEASURE_TYPE,
  filters: Record<string, unknown>,
): IMultiAnalysisMeasureOption[] => {
  const scopedOptions =
    measureType === MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND
      ? measureOptions.filter(option => option.useValue2)
      : measureOptions;

  return filterMeasureOptionsByCellFilters(scopedOptions, filters);
};

export const filterMeasureResponseByCellFilters = (
  response: IMultiAnalysisMeasureDataResponse | null | undefined,
  filters: Record<string, unknown>,
): IMultiAnalysisMeasureDataResponse => {
  if (!response) {
    return {};
  }

  if (!filters || Object.keys(filters).length === 0) {
    return response;
  }

  return Object.fromEntries(
    Object.entries(response).flatMap(([key, measure]) => {
      if (!measure || !Array.isArray(measure.data)) {
        return [];
      }

      const data = measure.data.filter(row => applyActiveMeasureCellFilters(row, filters));

      if (!data.length) {
        return [];
      }

      return [[key, { ...measure, data }]];
    }),
  );
};

interface IBuildMultiAnalysisTrendSeriesOptions {
  unitLabelFormatter?: (unitCode: number) => string;
  pointDirectionLabelFormatter?: (direction: number) => string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    return value === "1" || value.toLowerCase() === "true";
  }

  return false;
};

export const normalizeMultiAnalysisMeasureOptions = (response: unknown): IMultiAnalysisMeasureOption[] => {
  if (!Array.isArray(response)) {
    return [];
  }

  return response.flatMap(item => {
    if (!isRecord(item) || typeof item.title !== "string" || !item.title) {
      return [];
    }

    return [
      {
        title: item.title,
        description: typeof item.description === "string" ? item.description : null,
        useValue2: toBoolean(item.useValue2),
        type: Number.isFinite(Number(item.type ?? item.Type)) ? Number(item.type ?? item.Type) : null,
        rangeType: Number.isFinite(Number(item.rangeType ?? item.RangeType))
          ? Number(item.rangeType ?? item.RangeType)
          : null,
      },
    ];
  });
};

const toTimestamp = (value: MultiAnalysisTimeValue): number | null => {
  if (!value) {
    return null;
  }

  const normalized =
    value instanceof Date
      ? value.toISOString().replace("Z", "")
      : typeof value === "string"
        ? value.replace("Z", "")
        : value;
  const timestamp = new Date(normalized).getTime();

  return Number.isFinite(timestamp) ? timestamp : null;
};

const toRequestTimestamp = (value: Exclude<MultiAnalysisTimeValue, null | undefined>): string =>
  value instanceof Date ? value.toISOString() : value;

export const normalizeTrendRangeForRequest = (
  range: IMultiAnalysisTimeRange,
  dbTz = 0,
): IMultiAnalysisRequestTimeRange | null => {
  const queryRange = dbTz ? denormalizeMultiAnalysisGlobalRange(range, dbTz) : range;
  const start = toTimestamp(queryRange.start);
  const end = toTimestamp(queryRange.end);

  if (!queryRange.start || !queryRange.end || start === null || end === null) {
    return null;
  }

  const isReversed = start > end;

  return {
    startTimestamp: toRequestTimestamp(isReversed ? queryRange.end : queryRange.start),
    endTimestamp: toRequestTimestamp(isReversed ? queryRange.start : queryRange.end),
  };
};

export const filterTrendSeriesByRange = (series: IChartData[], range: IMultiAnalysisTimeRange): IChartData[] => {
  const start = toTimestamp(range.start);
  const end = toTimestamp(range.end);

  if (start === null || end === null) {
    return [];
  }

  const from = Math.min(start, end);
  const to = Math.max(start, end);

  return series
    .map(item => ({
      ...item,
      items: item.items.filter(({ date }) => {
        const timestamp = toTimestamp(date);

        return timestamp !== null && timestamp >= from && timestamp <= to;
      }),
    }))
    .filter(({ items }) => items.length > 0);
};

const normalizeSelectedPoints = (points: readonly IStructureTreePoint[]): IPointResponse[] =>
  points.map(point => ({
    id: point.id,
    title: point.title,
    Direction: point.direction,
  }));

const getMeasureTitle = (measure: IMultiAnalysisMeasureDataResponseItem, option?: IMultiAnalysisMeasureOption) =>
  option?.description && option.description !== measure.name ? option.description : measure.name;

const groupMeasureRowsByIdMeasure = (rows: readonly IMeasuresResponse[]): Map<number, IMeasuresResponse[]> => {
  const groups = new Map<number, IMeasuresResponse[]>();

  rows.forEach(row => {
    groups.set(row.idMeasure, [...(groups.get(row.idMeasure) ?? []), row]);
  });

  return groups;
};

const formatMeasureBandSuffix = (row: IMeasuresResponse | undefined): string => {
  if (!row) {
    return "";
  }

  const param1 = row.Param1;
  const param2 = row.Param2;

  if (
    param1 !== null &&
    param1 !== undefined &&
    param2 !== null &&
    param2 !== undefined &&
    Number.isFinite(Number(param1)) &&
    Number.isFinite(Number(param2))
  ) {
    return ` (${formatFrequencyRangeParam(Number(param1))}-${formatFrequencyRangeParam(Number(param2))})`;
  }

  return "";
};

const indexMeasureOptions = (measureOptions: readonly IMultiAnalysisMeasureOption[]) => {
  const optionsByTitle = new Map<string, IMultiAnalysisMeasureOption>();
  const orderByTitle = new Map<string, number>();

  measureOptions.forEach((option, index) => {
    if (!optionsByTitle.has(option.title)) {
      optionsByTitle.set(option.title, option);
    }

    if (!orderByTitle.has(option.title)) {
      orderByTitle.set(option.title, index);
    }
  });

  return { optionsByTitle, orderByTitle };
};

export const buildMultiAnalysisTrendSeries = (
  response: IMultiAnalysisMeasureDataResponse | null | undefined,
  points: readonly IStructureTreePoint[],
  measureOptions: readonly IMultiAnalysisMeasureOption[],
  options: IBuildMultiAnalysisTrendSeriesOptions = {},
): IChartData[] => {
  if (!response || points.length === 0) {
    return [];
  }

  const chartPoints = normalizeSelectedPoints(points);
  const { optionsByTitle, orderByTitle } = indexMeasureOptions(measureOptions);
  const measures = Object.values(response)
    .filter(
      (measure): measure is IMultiAnalysisMeasureDataResponseItem =>
        typeof measure?.name === "string" && Array.isArray(measure.data) && measure.data.length > 0,
    )
    .sort(
      (left, right) =>
        (orderByTitle.get(left.name) ?? Number.MAX_SAFE_INTEGER) -
        (orderByTitle.get(right.name) ?? Number.MAX_SAFE_INTEGER),
    );
  const isSingleMeasure = measures.length === 1;

  return measures.flatMap(measure => {
    const measureOption = optionsByTitle.get(measure.name);
    const measureTitle = getMeasureTitle(measure, measureOption);
    const idMeasureGroups = groupMeasureRowsByIdMeasure(measure.data);
    const hasMultipleIdMeasures = idMeasureGroups.size > 1;

    return Array.from(idMeasureGroups.entries()).flatMap(([idMeasure, rows]) => {
      const bandSuffix = hasMultipleIdMeasures ? formatMeasureBandSuffix(rows[0]) : "";
      const { chart } = transformMeasuresDataMultiplePoints(
        rows,
        chartPoints,
        measure.name,
        PARAMETER_TYPE.point,
        measureOption?.useValue2 ?? false,
      );

      return chart.map(series => ({
        ...series,
        idMeasure,
        name: `${series.name}${
          series.pointDirection
            ? ` ${options.pointDirectionLabelFormatter?.(series.pointDirection) ?? series.pointDirection}`
            : ""
        }${isSingleMeasure ? "" : ` / ${measureTitle}`}${bandSuffix}`,
        unitCode: series.unitCode ?? null,
        units:
          options.unitLabelFormatter && Number.isFinite(Number(series.units))
            ? options.unitLabelFormatter(Number(series.units))
            : series.units,
      }));
    });
  });
};
